// Atlas palette, colour blending and payload types, shared by the full atlas
// (/overview) and the landing-page hero. No sigma imports here: the landing
// page pulls this module into the main bundle.

export const ATLAS_URL = '/api/fastapi/graph/atlas/';
export const ATLAS_LOCATE_URL = '/api/fastapi/graph/atlas-locate';

// Neon two-tone palette. Colour says what kind of node it is, not which
// island: actors magenta, families cyan, IOCs one dim blue, the rings muted.
// Islands read by position, glow and name. (Per-island colours made the map
// a rainbow.)
export const CALM = {
  actor: '#ff4fd8',
  family: '#2ee6ff',
  dust: '#4a78d8',
  ring: { k: '#ffd36b', p: '#ff8f6b', t: '#e0a050', c: '#a86bf0', s: '#3ddc98' } as Record<string, string>,
  glow: '#00d4ff',
  label: '#2ee6ff',
};

// sigma blends with gl.ONE / ONE_MINUS_SRC_ALPHA on colours that are not
// premultiplied, so a translucent colour still adds its full brightness and
// thousands of faint edges stack up to white. Pre-blend every colour onto the
// stage background and hand sigma opaque colours only.
export const BG = [6, 10, 24];
export const hexRgb = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};
export const solid = (rgb: number[], a: number) =>
  `rgb(${rgb.map((v, i) => Math.round(BG[i] + (v - BG[i]) * a)).join(',')})`;
const GREY = [150, 145, 178];
/** Pull a colour toward the map's violet-grey (amt 0..1), optionally dimmed. */
export const towardGrey = (hex: string, amt: number, dim = 1) =>
  '#' + hexRgb(hex)
    .map((v, i) => Math.max(0, Math.min(255, Math.round((v + (GREY[i] - v) * amt) * dim))))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('');
/** Muted, opaque version of a type colour (IOC dust in "By type", donut slices). */
export const muted = (hex: string) => towardGrey(hex, 0.6, 0.95);
/** Translucent colour for the 2D canvas underlay only, never for sigma. */
export const hexA = (hex: string, a: number) => {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
};

// ── payload shapes (see api_server/atlas.py) ────────────────────────────────
/** IOC types counted in an entity's mix, in the server's disc order. */
export const MIX_ORDER = ['d', 'i', 'u', 'e', 'n', 'h', 'w', 'o'] as const;
/** [label, kind, x, y, cluster rank (-1 = lone), degree, own IOCs, country,
 *  IOC mix (counts in MIX_ORDER), home region (-1 = none),
 *  [first, last] report month ([0, 0] = undated)] */
export type EntityRow = [string, 'a' | 'f', number, number, number, number, number, string, number[], number, [number, number]?];
/** The time slider's months, from the Malpedia launch to the latest report:
 *  per month the entities with a report (active) and those first reported
 *  then (new); `before` were first reported before the launch. */
export interface MonthSpan { from: number; to: number; active: number[]; new: number[]; before: number }

// ── months ─────────────────────────────────────────────────────────────────
// The atlas counts time in months since year 0: year * 12 + month - 1 (0 = undated).
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const yearOf = (m: number) => Math.floor(m / 12);
/** "Mar 2021" */
export const fmtMonth = (m: number) => `${MON[m % 12]} ${yearOf(m)}`;
/** "2021-03", for the URL */
export const monthParam = (m: number) => `${yearOf(m)}-${String((m % 12) + 1).padStart(2, '0')}`;
/** "2021-03" → month index, or null */
export function parseMonth(v: string | null): number | null {
  const m = /^(\d{4})-(\d{2})$/.exec(v ?? '');
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  return y > 1990 && mo >= 1 && mo <= 12 ? y * 12 + mo - 1 : null;
}
/** A first–last month range as text: "Mar 2014 – Oct 2026", or null when undated. */
export function fmtSpan(span: [number, number] | undefined): string | null {
  if (!span || !span[0]) return null;
  return span[0] === span[1] ? fmtMonth(span[0]) : `${fmtMonth(span[0])} – ${fmtMonth(span[1])}`;
}
/** [file, x, y, r, IOC count]: an island's IOCs (or a tile of loners' IOCs). */
export type RegionRow = [string, number, number, number, number];
export interface Cluster { name: string; size: number; iocs: number; x: number; y: number; r: number }
export interface AtlasCore {
  entities: EntityRow[];
  coreEdges: [number, number, 0 | 1][];
  clusters: Cluster[];
  regions: RegionRow[];
  iocTotal: number;
  /** [id, name, x, y, entity idx…, first month] */
  techniques: [string, string, number, number, number[], number?][];
  /** [id, x, y, entity idx… (exploiting or named with it), first month,
   *  the exploiting ones (judged EXPLOITS)] */
  cves: [string, number, number, number[], number?, number[]?][];
  /** [id, org, country, x, y, IOCs, first month, owners of its IOCs (top 8)] */
  asns: [string, string, string, number, number, number, number?, number[]?][];
  /** [ISO-2, x, y, actor idx…, first month] (absent in older builds) */
  countries?: [string, number, number, number[], number][];
  /** ATT&CK campaigns (absent in older builds) */
  campaigns?: CampaignRow[];
  center: [number, number];
  rings: { core: number; k?: number; t: number; c: number; a: number };
  typeCounts: Record<string, number>;
  /** absent in builds before the monthly time slider */
  months?: MonthSpan;
  builtAt?: string;
}

/** An ATT&CK campaign: its parts, each with the month it was first reported
 *  for the campaign, so it can be replayed as it was uncovered. */
export interface CampaignRow {
  id: string;
  name: string;
  aliases: string[];
  /** first / last seen (activity), months */
  first: number;
  last: number;
  /** the replay's range: activity and reporting */
  span: [number, number];
  url: string;
  desc: string;
  /** [entity idx, month] actors and families */
  members: [number, number][];
  /** [technique idx, month] */
  techniques: [number, number][];
  /** [region, row, month] IOCs of its cited reports that are on the map */
  iocs: [number, number, number][];
  iocTotal: number;
  /** [month, title, cite_key ('' = not in the corpus), url] */
  reports: [number, string, string, string][];
}

/** core.json, or null when the atlas has not been built yet (404). */
export async function fetchAtlasCore(signal?: AbortSignal): Promise<AtlasCore | null> {
  const resp = await fetch(ATLAS_URL + 'core.json', { credentials: 'same-origin', signal });
  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error(`core.json: ${resp.status}`);
  return (await resp.json()) as AtlasCore;
}
